import { useCallback } from 'react';

import { IconX } from '@tabler/icons-react';
import Select, {
  OptionProps,
  InputProps,
  components,
  MenuProps,
  ControlProps,
  Props as SelectProps,
  SingleValue,
  MultiValue,
  ActionMeta,
} from 'react-select';

import { PillButton } from 'ui-kit/Buttons';
import Input from '../inputs/Input/Input';

import './PillButtonCombobox.scss';

export type PillButtonComboboxProps<T, Multi extends boolean> = {
  options: T[];
  value?: T | T[];
  manuLabel?: string;
  comboLabel?: string;
  inputPlaceholder?: string;
  onClose?: () => void;
  onValueSelected: Exclude<SelectProps<T>['onChange'], undefined>;
  getOptionLabel?: SelectProps<T>['getOptionLabel'];
  getOptionValue?: SelectProps<T>['getOptionValue'];
  filterOption?: SelectProps<T>['filterOption'];
  isMulti?: Multi;
};

export function PillButtonCombobox<T extends object, Multi extends boolean>(
  props: PillButtonComboboxProps<T, Multi>,
) {
  const {
    onValueSelected,
    onClose,
    comboLabel,
    manuLabel,
    inputPlaceholder,
    value,
    ...selectProps
  } = props;

  function handleValueChange(value: SingleValue<T> | MultiValue<T>, action: ActionMeta<T>) {
    if (!value) {
      return;
    }
    onValueSelected(value, action);
    if (!selectProps.isMulti) {
      onClose?.();
    }
  }

  const boundControl = useCallback(
    (props: ControlProps<T, Multi>) => (
      <Control<T, Multi> {...props} label={comboLabel} onCloseBtnClick={onClose} />
    ),
    [comboLabel, onClose],
  );
  const boundInput = useCallback(
    (props: InputProps<T, Multi>) => (
      <ComboInput<T, Multi> {...props} inputPlaceholder={inputPlaceholder} />
    ),
    [inputPlaceholder],
  );
  const boundMenu = useCallback(
    (props: MenuProps<T, Multi>) => <Menu<T, Multi> {...props} label={manuLabel} />,
    [manuLabel],
  );
  const boundOption = useCallback(
    (props: OptionProps<T, Multi>) => (
      <OptionComp<T, Multi> {...props} onValueSelected={handleValueChange} />
    ),
    [handleValueChange],
  );
  const boundNoOptions = useCallback(
    () => <div className="pill-combo__no_results">No results found</div>,
    [],
  );

  return (
    <Select<T, Multi>
      {...selectProps}
      defaultValue={value}
      unstyled={true}
      onChange={handleValueChange}
      isSearchable={true}
      className="pill-combo"
      menuIsOpen={true}
      maxMenuHeight={180}
      components={{
        Control: boundControl,
        Input: boundInput,
        Option: boundOption,
        DropdownIndicator: () => null,
        Menu: boundMenu,
        Placeholder: () => null,
        NoOptionsMessage: boundNoOptions,
      }}
      styles={{
        menu: () => ({}),
        valueContainer: (base) => ({ ...base, padding: 4 }),
      }}
      controlShouldRenderValue={false}
      backspaceRemovesValue={false}
      isClearable={false}
      escapeClearsValue={true}
      hideSelectedOptions={!selectProps.isMulti}
      tabSelectsValue={false}
    />
  );
}

function OptionComp<T, Multi extends boolean>(
  props: OptionProps<T, Multi> & {
    onValueSelected: Exclude<SelectProps<T>['onChange'], undefined>;
  },
) {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const currentValue = props.selectProps.value;
    const getOptionValue = props.selectProps.getOptionValue || ((option: T) => String(option));
    const optionValue = getOptionValue(props.data);
    let newValue: SingleValue<T> | MultiValue<T>;
    let action: ActionMeta<T>;

    if (props.isSelected) {
      if (props.selectProps.isMulti) {
        const currentArray = (currentValue as MultiValue<T>) || [];
        newValue = currentArray.filter(
          (item) => getOptionValue(item) !== optionValue,
        ) as MultiValue<T>;
      } else {
        newValue = null as SingleValue<T>;
      }
      action = {
        action: 'deselect-option',
        option: props.data,
        removedValue: props.data,
        name: props.selectProps.name,
      };
    } else {
      if (props.selectProps.isMulti) {
        const currentArray = (currentValue as MultiValue<T>) || [];
        newValue = [...currentArray, props.data] as MultiValue<T>;
      } else {
        newValue = props.data as SingleValue<T>;
      }
      action = {
        action: 'select-option',
        option: props.data,
        name: props.selectProps.name,
      };
    }

    props.onValueSelected(newValue, action);

    if (props.innerProps.onClick) {
      props.innerProps.onClick(e);
    }
  };

  const handleIconClick = () => {
    const syntheticEvent = {
      stopPropagation: () => {},
      preventDefault: () => {},
    } as React.MouseEvent<HTMLDivElement>;
    handleClick(syntheticEvent);
  };

  return (
    <div
      ref={props.innerRef}
      {...props.innerProps}
      className="pill-combo__option"
      role="option"
      aria-selected={props.isSelected}
      onClick={handleClick}
    >
      <PillButton
        isActive={props.isSelected}
        isFocused={props.isFocused}
        iconName={props.isSelected ? 'pill-close' : 'pill-plus'}
        description={props.label}
        iconOnClick={handleIconClick}
        showIconAlways={true}
      />
    </div>
  );
}

function ComboInput<T, Multi extends boolean>(
  props: InputProps<T, Multi> & { inputPlaceholder?: string },
) {
  // Prefix unused variables with an underscore to prevent ESLint warnings
  const {
    inputPlaceholder,
    capture: _capture,
    clearValue: _clearValue,
    getClassNames: _getClassNames,
    cx: _cx,
    getStyles: _getStyles,
    getValue: _getValue,
    hasValue: _hasValue,
    isHidden: _isHidden,
    isMulti: _isMulti,
    isRtl: _isRtl,
    selectOption: _selectOption,
    selectProps: _selectProps,
    setValue: _setValue,
    innerRef: _innerRef,
    inputClassName: _inputClassName,
    isDisabled: _isDisabled,
    ...inputProps
  } = props;
  return <Input ref={props.innerRef} placeholder={inputPlaceholder} autoFocus {...inputProps} />;
}

function Menu<T, Multi extends boolean>(props: MenuProps<T, Multi> & { label?: string }) {
  return (
    <>
      {props.label ? <div className="pill-combo__menu_desc">{props.label}</div> : null}
      <components.Menu {...props}>{props.children}</components.Menu>
    </>
  );
}

function Control<T, Multi extends boolean>(
  props: ControlProps<T, Multi> & {
    label?: string;
    onCloseBtnClick?: () => void;
  },
) {
  return (
    <>
      {props.label || props.onCloseBtnClick ? (
        <div className="pill-combo__header">
          <span className="pill-combo__label">{props.label}</span>
          <button
            aria-label="Close"
            className="bg-none"
            onClick={props.onCloseBtnClick}
            tabIndex={0}
          >
            <IconX color="white" size={20} />
          </button>
        </div>
      ) : null}
      <components.Control {...props}>{props.children}</components.Control>
    </>
  );
}
